import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const FUNCTION_URL = 'https://stellular-clafoutis-10452c.netlify.app/.netlify/functions/falabrasil-chat';

const SCENARIOS = [
  { id: 'entrevista', icon: '💼', nombre: 'Entrevista de trabajo' },
  { id: 'medico', icon: '🏥', nombre: 'Ir al médico o a la farmacia' },
  { id: 'banco', icon: '🏦', nombre: 'Trámites de banco' },
  { id: 'alquiler', icon: '🏠', nombre: 'Hablar con el casero' },
  { id: 'emergencia', icon: '🚨', nombre: 'Una emergencia' },
];

const LEVELS = [
  { id: 'basico', nombre: 'Básico', desc: 'Sobrevivir la conversación' },
  { id: 'intermedio', nombre: 'Intermedio', desc: 'Conversación completa' },
  { id: 'avanzado', nombre: 'Avanzado', desc: 'Fluidez y matices culturales', pro: true },
];

function formatMessage(text) {
  let safe = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  safe = safe.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  safe = safe.replace(/\n/g, '<br/>');
  return safe;
}

function inlineMd(text) {
  let t = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  t = t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/\*(.+?)\*/g, '<em style="color:var(--orange);font-style:normal;">$1</em>');
  return t;
}

function lessonMdToHtml(md) {
  const lines = md.split('\n');
  let html = '';
  let listType = null; // 'ul' | 'ol' | null

  const closeList = () => {
    if (listType) { html += listType === 'ul' ? '</ul>' : '</ol>'; listType = null; }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    const isTableRow = trimmed.startsWith('|');
    const nextIsSeparator = lines[i + 1] && /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(lines[i + 1].trim());
    if (isTableRow && nextIsSeparator) {
      closeList();
      const splitRow = (row) => row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
      const headerCells = splitRow(line);
      let table = '<div style="overflow-x:auto;margin:10px 0;"><table style="border-collapse:collapse;width:100%;font-size:13px;"><thead><tr>';
      table += headerCells.map(c => `<th style="text-align:left;padding:6px 10px;border-bottom:2px solid var(--orange);color:var(--orange);white-space:nowrap;">${inlineMd(c)}</th>`).join('');
      table += '</tr></thead><tbody>';
      i += 2;
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const cells = splitRow(lines[i]);
        table += '<tr>' + cells.map(c => `<td style="padding:6px 10px;border-bottom:1px solid var(--navy-border);">${inlineMd(c)}</td>`).join('') + '</tr>';
        i++;
      }
      table += '</tbody></table></div>';
      html += table;
      continue;
    }

    if (/^###\s+/.test(trimmed)) { closeList(); html += `<h4 style="color:var(--white);font-size:14px;margin:14px 0 6px;">${inlineMd(trimmed.replace(/^###\s+/, ''))}</h4>`; i++; continue; }
    if (/^##\s+/.test(trimmed)) { closeList(); html += `<h3 style="color:var(--orange);font-size:15px;margin:16px 0 8px;">${inlineMd(trimmed.replace(/^##\s+/, ''))}</h3>`; i++; continue; }
    if (/^#\s+/.test(trimmed)) { closeList(); html += `<h2 style="color:var(--white);font-size:17px;margin:16px 0 8px;">${inlineMd(trimmed.replace(/^#\s+/, ''))}</h2>`; i++; continue; }
    if (/^---+\s*$/.test(trimmed)) { closeList(); html += '<hr style="border:none;border-top:1px solid var(--navy-border);margin:14px 0;"/>'; i++; continue; }

    if (/^⚠️/.test(trimmed)) {
      closeList();
      html += `<p style="background:rgba(244,121,32,0.12);border-left:3px solid var(--orange);padding:8px 12px;border-radius:6px;margin:8px 0;">${inlineMd(trimmed)}</p>`;
      i++; continue;
    }

    if (/^-\s+/.test(trimmed)) {
      if (listType !== 'ul') { closeList(); html += '<ul style="margin:6px 0;padding-left:20px;">'; listType = 'ul'; }
      html += `<li style="margin-bottom:4px;">${inlineMd(trimmed.replace(/^-\s+/, ''))}</li>`;
      i++; continue;
    }
    if (/^\d+\.\s+/.test(trimmed)) {
      if (listType !== 'ol') { closeList(); html += '<ol style="margin:6px 0;padding-left:20px;">'; listType = 'ol'; }
      html += `<li style="margin-bottom:4px;">${inlineMd(trimmed.replace(/^\d+\.\s+/, ''))}</li>`;
      i++; continue;
    }

    if (trimmed === '') { closeList(); i++; continue; }

    closeList();
    html += `<p style="margin:8px 0;">${inlineMd(trimmed)}</p>`;
    i++;
  }
  closeList();
  return html;
}

async function callFunction(body) {
  const res = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

function ProLock() {
  return (
    <span style={{
      background: 'var(--gold)', color: 'var(--navy)', fontSize: 10, fontWeight: 800,
      padding: '2px 6px', borderRadius: 8, marginLeft: 8,
    }}>PRO</span>
  );
}

function BackHeader({ title, subtitle, onBack }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '16px', borderBottom: '1px solid var(--navy-border)', background: 'var(--bg)',
    }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--white)', fontSize: 20, cursor: 'pointer' }}>←</button>
      <div>
        <div style={{ color: 'var(--white)', fontWeight: 800, fontSize: 16 }}>{title}</div>
        {subtitle && <div style={{ color: 'var(--muted)', fontSize: 12 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

export default function FalaBrasilChat({ onBack }) {
  const { user } = useAuth();
  const chatId = user?.uid;
  const [view, setView] = useState('menu'); // menu | scenarios | levels | chat | lessons | lessonView | saved
  const [scenario, setScenario] = useState(null);
  const [level, setLevel] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [startingSession, setStartingSession] = useState(false);
  const sendingRef = useRef(false);
  const scrollRef = useRef(null);

  const [lessons, setLessons] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [savedPhrases, setSavedPhrases] = useState(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const goMenu = () => {
    setView('menu');
    setScenario(null);
    setLevel(null);
    setSessionId(null);
    setSessionEnded(false);
    setMessages([]);
  };

  const confirmLeaveSession = () => {
    const hasUnfinishedProgress = sessionId && !sessionEnded && messages.some(m => m.role === 'user');
    if (hasUnfinishedProgress && !window.confirm('¿Seguro que quieres salir? Vas a perder esta práctica.')) {
      return false;
    }
    return true;
  };

  const openWhatsappPro = () => {
    window.open('https://api.whatsapp.com/send?phone=5541920004775&text=' + encodeURIComponent('Hola, quiero suscribirme al plan Pro de Asesores (R$19,90/mes)'), '_blank');
  };

  const chooseLevel = async (lvl) => {
    if (!chatId || startingSession) return;
    setLevel(lvl.id);
    setSessionEnded(false);
    setStartingSession(true);
    try {
      const data = await callFunction({ chatId, mode: 'startSession', scenario: scenario.id, level: lvl.id });
      if (data.limitReached) {
        setMessages([{ role: 'assistant', content: data.message, isPaywall: true }]);
      } else if (data.reply) {
        setSessionId(data.sessionId);
        setMessages([{ role: 'assistant', content: data.reply }]);
      } else {
        setMessages([{ role: 'assistant', content: 'No se pudo iniciar la práctica. Intenta de nuevo.' }]);
      }
    } catch (err) {
      setMessages([{ role: 'assistant', content: 'No se pudo conectar. Revisa tu internet e intenta de nuevo.' }]);
    } finally {
      setStartingSession(false);
      setView('chat');
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading || sendingRef.current || !sessionId) return;
    sendingRef.current = true;
    setLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    try {
      const data = await callFunction({ chatId, sessionId, scenario: scenario.id, level, message: text });
      if (data.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      }
      if (data.sessionClosed && data.frasesClave) {
        setMessages(prev => [...prev, { role: 'assistant', content: '📌 Frases para llevarte de esta práctica:\n' + data.frasesClave, isFrases: true }]);
        setSessionEnded(true);
      }
      if (data.suggestLevelUp) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Ya practicaste bastante este nivel en "${scenario.nombre}". ¿Probamos ${data.suggestLevelUp} la próxima vez?` }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No se pudo conectar. Intenta de nuevo.' }]);
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const loadLessons = async () => {
    setView('lessons');
    if (lessons) return;
    try {
      const data = await callFunction({ chatId, mode: 'listLessons' });
      setLessons(data.lessons || []);
    } catch (err) {
      setLessons([]);
    }
  };

  const loadSavedPhrases = async () => {
    setView('saved');
    if (!chatId) return;
    try {
      const data = await callFunction({ chatId, mode: 'listSavedPhrases' });
      setSavedPhrases(data.sessions || []);
    } catch (err) {
      setSavedPhrases([]);
    }
  };

  const askLessonQuestion = async (lessonNumero, question, setAnswer) => {
    setAnswer('Pensando...');
    try {
      const data = await callFunction({ chatId, mode: 'lessonQuestion', lessonNumero, message: question });
      setAnswer(data.reply || 'No se pudo responder. Intenta de nuevo.');
    } catch (err) {
      setAnswer('No se pudo conectar. Intenta de nuevo.');
    }
  };

  // ---------- MENU ----------
  if (view === 'menu') {
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', background: 'var(--bg)', padding: '24px 16px 100px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--white)', fontSize: 20, cursor: 'pointer' }}>←</button>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 900, color: 'var(--white)' }}>🗣️ Fala Brasil</h1>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Portugués para tu vida real en Brasil</p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <button onClick={() => setView('scenarios')} style={cardStyle}>
            <span style={{ fontSize: 30 }}>🎭</span>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={cardTitle}>Practicar una situación</div>
              <div style={cardDesc}>Simula una entrevista, el médico, el banco y más</div>
            </div>
            <span style={{ color: 'var(--orange)', fontSize: 18 }}>›</span>
          </button>

          <button onClick={loadLessons} style={cardStyle}>
            <span style={{ fontSize: 30 }}>📖</span>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={cardTitle}>Clases de portugués</div>
              <div style={cardDesc}>Curso paso a paso, de cero a fluido</div>
            </div>
            <span style={{ color: 'var(--orange)', fontSize: 18 }}>›</span>
          </button>

          <button onClick={loadSavedPhrases} style={cardStyle}>
            <span style={{ fontSize: 30 }}>⭐</span>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={cardTitle}>Mis frases guardadas</div>
              <div style={cardDesc}>Repasa antes de tu cita o entrevista real</div>
            </div>
            <span style={{ color: 'var(--orange)', fontSize: 18 }}>›</span>
          </button>
        </div>
      </div>
    );
  }

  // ---------- SCENARIOS ----------
  if (view === 'scenarios') {
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
        <BackHeader title="Elige una situación" onBack={goMenu} />
        <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {SCENARIOS.map(s => (
            <button key={s.id} onClick={() => { setScenario(s); setView('levels'); }} style={cardStyle}>
              <span style={{ fontSize: 28 }}>{s.icon}</span>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={cardTitle}>{s.nombre}</div>
              </div>
              <span style={{ color: 'var(--orange)', fontSize: 18 }}>›</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- LEVELS ----------
  if (view === 'levels') {
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
        <BackHeader title={scenario.nombre} subtitle="Elige tu nivel" onBack={() => setView('scenarios')} />
        <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {LEVELS.map(lvl => (
            <button key={lvl.id} disabled={startingSession} onClick={() => chooseLevel(lvl)} style={{ ...cardStyle, opacity: startingSession ? 0.6 : 1 }}>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={cardTitle}>{lvl.nombre}{lvl.pro && <ProLock />}</div>
                <div style={cardDesc}>{lvl.desc}</div>
              </div>
              <span style={{ color: 'var(--orange)', fontSize: 18 }}>›</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- CHAT (roleplay) ----------
  if (view === 'chat') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
        <BackHeader title={scenario.nombre} subtitle={LEVELS.find(l => l.id === level)?.nombre} onBack={() => { if (confirmLeaveSession()) setView('levels'); }} />
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '85%',
                background: m.role === 'user' ? 'var(--orange)' : (m.isFrases ? 'var(--gold)' : 'var(--navy-card)'),
                color: m.isFrases ? 'var(--navy)' : 'var(--white)',
                padding: '10px 14px', borderRadius: 14, fontSize: 14, lineHeight: 1.5,
                border: m.isPaywall ? '1px solid var(--orange)' : 'none',
              }} dangerouslySetInnerHTML={{ __html: formatMessage(m.content) }} />
              {m.isPaywall && (
                <button onClick={openWhatsappPro} style={proButtonStyle}>Quiero ser Pro</button>
              )}
            </div>
          ))}
          {(loading || startingSession) && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Escribiendo...</div>}
          <div ref={scrollRef} />
        </div>
        {sessionId && (
          <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--navy-border)' }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Responde en portugués..."
              style={inputStyle}
            />
            <button onClick={sendMessage} disabled={loading || !input.trim()} style={{ ...sendButtonStyle, opacity: (loading || !input.trim()) ? 0.5 : 1 }}>➤</button>
          </div>
        )}
      </div>
    );
  }

  // ---------- LESSONS LIST ----------
  if (view === 'lessons') {
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
        <BackHeader title="Clases de portugués" onBack={goMenu} />
        <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {lessons === null && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando...</div>}
          {lessons?.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Todavía no hay clases disponibles.</div>}
          {lessons?.map(l => (
            <button key={l.numero} onClick={() => { setSelectedLesson(l); setView('lessonView'); }} style={cardStyle}>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={cardDesc}>Lección {l.numero}</div>
                <div style={cardTitle}>{l.titulo}</div>
              </div>
              {l.locked ? <ProLock /> : <span style={{ color: 'var(--orange)', fontSize: 18 }}>›</span>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- LESSON VIEW ----------
  if (view === 'lessonView') {
    return <LessonView lesson={selectedLesson} onBack={() => setView('lessons')} onAsk={askLessonQuestion} />;
  }

  // ---------- SAVED PHRASES ----------
  if (view === 'saved') {
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
        <BackHeader title="Mis frases guardadas" onBack={goMenu} />
        <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {savedPhrases === null && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando...</div>}
          {savedPhrases?.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Todavía no completaste ninguna práctica.</div>}
          {savedPhrases?.map((s, i) => (
            <div key={i} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 14 }}>
              <div style={{ color: 'var(--orange)', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                {SCENARIOS.find(sc => sc.id === s.scenario)?.nombre || s.scenario} · {LEVELS.find(l => l.id === s.level)?.nombre}
              </div>
              <div style={{ color: 'var(--white)', fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{s.frasesClave}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

function LessonView({ lesson, onBack, onAsk }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);

  const submitQuestion = async () => {
    const text = question.trim();
    if (!text || asking) return;
    setAsking(true);
    await onAsk(lesson.numero, text, setAnswer);
    setAsking(false);
    setQuestion('');
  };

  if (lesson.locked) {
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
        <BackHeader title={`Lección ${lesson.numero}`} subtitle={lesson.titulo} onBack={onBack} />
        <div style={{ padding: '16px 16px 100px' }}>
          <div style={{
            color: 'var(--white)', fontSize: 14, lineHeight: 1.6, textAlign: 'center',
            background: 'var(--navy-card)', border: '1px solid var(--orange)', borderRadius: 14, padding: 24,
          }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
            <div style={{ marginBottom: 14 }}>Esta lección es exclusiva de Pro. Con Pro tienes las 22 lecciones completas del curso.</div>
            <button
              onClick={() => window.open('https://api.whatsapp.com/send?phone=5541920004775&text=' + encodeURIComponent('Hola, quiero suscribirme al plan Pro de Asesores (R$19,90/mes)'), '_blank')}
              style={proButtonStyle}
            >
              Quiero ser Pro
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
      <BackHeader title={`Lección ${lesson.numero}`} subtitle={lesson.titulo} onBack={onBack} />
      <div style={{ padding: '16px 16px 100px' }}>
        <div
          style={{
            color: 'var(--white)', fontSize: 14, lineHeight: 1.6,
            background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16,
          }}
          dangerouslySetInnerHTML={{ __html: lessonMdToHtml(lesson.contenido) }}
        />

        <div style={{ marginTop: 20 }}>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 8 }}>¿Tienes una duda sobre esta lección?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitQuestion(); }}
              placeholder="Pregúntale a Fala Brasil..."
              style={inputStyle}
            />
            <button onClick={submitQuestion} disabled={asking || !question.trim()} style={{ ...sendButtonStyle, opacity: (asking || !question.trim()) ? 0.5 : 1 }}>➤</button>
          </div>
          {answer && (
            <div style={{ marginTop: 12, color: 'var(--white)', fontSize: 14, lineHeight: 1.6, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 14 }}
              dangerouslySetInnerHTML={{ __html: formatMessage(answer) }} />
          )}
        </div>
      </div>
    </div>
  );
}

const cardStyle = {
  background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16,
  padding: '16px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', width: '100%',
};
const cardTitle = { fontSize: 15, fontWeight: 800, color: 'var(--white)' };
const cardDesc = { fontSize: 12, color: 'var(--muted)', marginTop: 2 };
const inputStyle = {
  flex: 1, padding: '12px 14px', borderRadius: 20, border: '1px solid var(--navy-border)',
  background: 'var(--navy-card)', color: 'var(--white)', fontSize: 14, outline: 'none',
};
const sendButtonStyle = {
  background: 'var(--orange)', color: 'var(--white)', border: 'none', borderRadius: '50%',
  width: 44, height: 44, fontSize: 18, cursor: 'pointer',
};
const proButtonStyle = {
  background: 'var(--orange)', color: 'var(--white)', border: 'none', borderRadius: 20,
  padding: '8px 16px', fontSize: 13, marginTop: 6, cursor: 'pointer',
};
